import React, { useEffect, useCallback, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { Box, Button } from 'roo-ui/components';
import { StepHeader, EDITING } from 'components/StepWizard';
import FullPointsPay from './FullPointsPay';
import DepositPointsPay from './DepositPointsPay';
import PaymentMode from './PaymentMode';
import theme from 'lib/theme';
import { getPointsErrors, getTravelPassErrors, getVoucherErrors } from 'store/booking/bookingSelectors';
import { useDataLayer } from 'hooks/useDataLayer';
import { PAYMENT_MODES } from 'lib/enums/payment';
import { getPaymentMode } from 'store/checkout/checkoutSelectors';
import PayLaterSummary from './PayLaterSummary';

const PaymentOptions = ({ step, ...rest }) => {
  const { updateFormData, hasState, edit, containerRef, stepNumber, completeStep } = step;

  const paymentMode = useSelector(getPaymentMode);
  const pointsErrors = useSelector(getPointsErrors);
  const voucherErrors = useSelector(getVoucherErrors);
  const travelPassErrors = useSelector(getTravelPassErrors);
  const { emitInteractionEvent } = useDataLayer();
  const hasErrors = !isEmpty(pointsErrors) || !isEmpty(voucherErrors) || !isEmpty(travelPassErrors);
  const hasErrorsRef = useRef(hasErrors);

  const isFullPaymentMode = paymentMode === PAYMENT_MODES.FULL;
  const isDepositPaymentMode = paymentMode === PAYMENT_MODES.DEPOSIT;

  useEffect(() => {
    // we only want to do this if the error state has changed. The edit function can be recreated by
    // the step wizard which will trigger this effect when we don't want it to run
    if (hasErrorsRef.current !== hasErrors && hasErrors) {
      edit();
    }
    hasErrorsRef.current = hasErrors;
  }, [edit, hasErrors, pointsErrors, travelPassErrors, voucherErrors]);

  const handleContinue = useCallback(() => {
    completeStep();
    emitInteractionEvent({ type: 'Continue Button', value: 'Payment Options Button Selected' });
  }, [emitInteractionEvent, completeStep]);

  return (
    <Box {...rest} ref={containerRef}>
      <Box bg="white" borderRadius="default" boxShadow="hard">
        <StepHeader title="Payment options" edit={edit} hasState={hasState} stepNumber={stepNumber} />

        {hasState(EDITING) && (
          <Box p={[4, 8]}>
            <PaymentMode updateFormData={updateFormData} />

            {paymentMode && (
              <Fragment>
                {isFullPaymentMode && <FullPointsPay />}
                {isDepositPaymentMode && (
                  <Fragment>
                    <DepositPointsPay />
                    <PayLaterSummary />
                  </Fragment>
                )}

                <Box pt={[4, 8]} mt={[4, 8]} borderTop={`1px solid ${theme.colors.greys.alto}`}>
                  <Button onClick={handleContinue} variant="primary" width={['100%', 'auto']} type="submit" data-testid="continue-button">
                    Continue
                  </Button>
                </Box>
              </Fragment>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

PaymentOptions.propTypes = {
  step: PropTypes.shape({
    updateFormData: PropTypes.func,
    hasState: PropTypes.func,
    edit: PropTypes.func,
    completeStep: PropTypes.func,
    containerRef: PropTypes.shape({ current: PropTypes.any }),
    stepNumber: PropTypes.number,
  }),
};

PaymentOptions.defaultProps = {
  step: {
    updateFormData: noop,
    hasState: noop,
    edit: noop,
  },
};

export default PaymentOptions;
